import useCartStore from "../api/cart";
import { getToken, setToken } from "./staffAuth";
import { loadStaffPrefs, saveStaffPrefs } from "./staffPrefs";

/**
 * Shared-device ("kiosk") session hygiene for the popup app.
 *
 * Popup staff run many customers through one browser, so anything the browser
 * remembers about the previous customer can leak into the next order. These
 * helpers wipe everything the app itself controls:
 *  - the zustand cart store and every sessionStorage/localStorage key except
 *    the two that describe the device itself (staff check-in defaults and the
 *    staff login)
 *  - first-party cookies (HubSpot's hubspotutk/__hs* identity cookies and GA's
 *    _ga* are the ones that matter – they tie every customer on the device to
 *    the first one's marketing identity)
 *  - a page resurrected from the browser's back/forward cache after payment
 *
 * Browser autofill and third-party (stripe.com) cookies are out of reach of
 * any web page; Stripe Link is disabled server-side for popup payments instead.
 */

// Cookies HubSpot's tracking script (public/index.html) sets on this domain.
const HUBSPOT_COOKIES = ["hubspotutk", "__hstc", "__hssc", "__hssrc"];

const EXPIRED = "expires=Thu, 01 Jan 1970 00:00:00 GMT";

const deleteCookie = (name) => {
  const parts = window.location.hostname.split(".");
  // No domain attribute first, then every domain the cookie could have been set
  // on (HubSpot uses the registrable domain, e.g. ".thefxry.com").
  const domains = [null];
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(parts.slice(i).join("."));
  }
  domains.forEach((domain) => {
    document.cookie = `${name}=; ${EXPIRED}; path=/${
      domain ? `; domain=${domain}` : ""
    }`;
  });
};

const clearFirstPartyCookies = () => {
  const names = new Set(HUBSPOT_COOKIES);
  document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter(Boolean)
    .forEach((name) => names.add(name));
  names.forEach(deleteCookie);
};

/** Forget everything this browser knows about the current/previous customer. */
export const resetKioskSession = () => {
  try {
    useCartStore.setState({ cartItems: [] });
    useCartStore.persist?.clearStorage?.();
  } catch (error) {
    console.error("[KIOSK] Failed to reset cart store:", error);
  }
  try {
    // Two things are kept, and both describe the device and the staff member
    // rather than the customer: the check-in defaults (location, checked in
    // by, visit type — utils/staffPrefs.js) and the staff login token
    // (utils/staffAuth.js). Without the second, staff would be signed out
    // after every order they took. Nothing about a customer may join them.
    const staffPrefs = loadStaffPrefs();
    const staffToken = getToken();
    sessionStorage.clear();
    localStorage.clear();
    if (Object.keys(staffPrefs).length > 0) {
      saveStaffPrefs(staffPrefs);
    }
    if (staffToken) {
      setToken(staffToken);
    }
  } catch (error) {
    console.error("[KIOSK] Failed to clear web storage:", error);
  }
  try {
    clearFirstPartyCookies();
  } catch (error) {
    console.error("[KIOSK] Failed to clear cookies:", error);
  }
};

/**
 * Stripe redirects to /thankyou with a full navigation, so the browser may keep
 * the filled-in checkout document alive in its back/forward cache and bring it
 * back – form, cart and all – if anyone presses Back. No script runs on such a
 * restore except `pageshow`, so that is where a restored document is discarded.
 * Returns the cleanup function (for use directly in a useEffect).
 */
export const installBfCacheGuard = () => {
  const onPageShow = (event) => {
    if (!event.persisted) {
      return;
    }
    resetKioskSession();
    window.location.replace("/");
  };
  window.addEventListener("pageshow", onPageShow);
  return () => window.removeEventListener("pageshow", onPageShow);
};
